import { useMemo, type ReactNode } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';
import { foldSearch } from '@eis/core/reporting';
import type { DatasetRef } from '../../../lib/document';
import { useDatasets } from '../api/reports';

/** One dataset a report can read: a catalog view or a published custom dataset. */
export interface DatasetOption {
  readonly key: string;
  readonly label: string;
  /** The heading the option is listed under: the catalog group, or "Eigene Datensätze". */
  readonly group: string;
  readonly ref: DatasetRef;
}

export function datasetKey(ref: DatasetRef): string {
  return ref.kind === 'CATALOG' ? `CATALOG:${ref.name}` : `CUSTOM:${ref.code}`;
}

/** The workspace's datasets as picker options: custom datasets first, then the catalog by group. */
export function useDatasetOptions(): {
  readonly options: DatasetOption[];
  readonly loading: boolean;
} {
  const { t } = useTranslation('reporting');
  const datasets = useDatasets();
  const options = useMemo(() => {
    const custom = (datasets.data?.customDatasets ?? [])
      .filter((d) => (d.publishedVersion ?? 0) > 0)
      .map((d) => ({
        key: `CUSTOM:${d.code ?? ''}`,
        label: `${d.name ?? ''} (v${String(d.publishedVersion ?? 0)})`,
        group: t('datasetPicker.custom'),
        ref: { kind: 'CUSTOM' as const, code: d.code ?? '', version: d.publishedVersion ?? 1 },
      }));
    const catalog = (datasets.data?.datasets ?? [])
      .filter((d) => (d.productVersion ?? 0) === 0)
      .map((d) => ({
        key: `CATALOG:${d.name ?? ''}`,
        label: d.name ?? '',
        group: d.group === undefined || d.group === '' ? t('datasetPicker.catalog') : d.group,
        ref: { kind: 'CATALOG' as const, name: d.name ?? '' },
      }))
      .sort((a, b) => a.group.localeCompare(b.group) || a.label.localeCompare(b.label));
    return [...custom, ...catalog];
  }, [datasets.data, t]);
  return { options, loading: datasets.isLoading };
}

export interface DatasetPickerProps {
  readonly value: string;
  readonly onChange: (option: DatasetOption | undefined) => void;
  readonly label?: string;
  /** The catalog's views only: a custom dataset's sources (S-1) are views, never another custom dataset. */
  readonly catalogOnly?: boolean;
}

/**
 * The dataset choice of "Neuer Bericht", the editor's "Datensatz wechseln" and a custom dataset's
 * sources (owner feedback 2026-11-01): a searchable list — a workspace has hundreds of catalog views — grouped by the
 * catalog group, matched accent- and case-insensitively on every word typed.
 */
export function DatasetPicker({
  value,
  onChange,
  label,
  catalogOnly = false,
}: DatasetPickerProps): ReactNode {
  const { t } = useTranslation('reporting');
  const all = useDatasetOptions();
  const loading = all.loading;
  const options = catalogOnly ? all.options.filter((o) => o.ref.kind === 'CATALOG') : all.options;
  const selected = options.find((o) => o.key === value) ?? null;
  return (
    <Autocomplete
      size="small"
      options={options}
      value={selected}
      loading={loading}
      groupBy={(o) => o.group}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(a, b) => a.key === b.key}
      filterOptions={(list, state) => {
        const terms = foldSearch(state.inputValue.trim());
        if (terms.length === 0) return list;
        return list.filter((o) => {
          const text = foldSearch(`${o.label} ${o.group}`).join(' ');
          return terms.every((term) => text.includes(term));
        });
      }}
      noOptionsText={t('datasetPicker.noMatch')}
      loadingText={t('datasetPicker.loading')}
      onChange={(_, option) => {
        onChange(option ?? undefined);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label ?? t('newDialog.dataset')}
          placeholder={t('datasetPicker.placeholder')}
        />
      )}
    />
  );
}
